/**
 * "Your request" — the INPUT side of a run as a collapsible summary strip:
 * one compact line (brief preview · attachments · who/when) that expands to
 * the full brief, the launch attachments, and the HITL conversation with each
 * reply rendered UNDER the question that prompted it (questions come from the
 * run's trace via `questionsAsked`, so answers never appear context-free).
 *
 * Mounted at the TOP of the run page — context first, one line tall until the
 * reader wants more. Rendered only when the BFF has an input record
 * (portal-launched runs); mail-originated runs simply have no card.
 */
import { Icon, iconForMime } from '@evt/flow-stream-ui'
import type { RunFileMeta, RunInputReplyView, RunInputView } from '@portal/contracts'
import { useId, useState } from 'react'
import { runInputFileUrl, runReplyFileUrl } from '../api/runs'
import { useI18n } from '../i18n'
import type { AskedQuestion } from './journey'

export interface RunRequestProps {
  input: RunInputView
  /** HITL questions (from the trace) — pairs question `round` with reply `index + 1`. */
  questions?: AskedQuestion[]
  defaultOpen?: boolean
  /**
   * False while the record isn't persisted yet (the just-launched composer
   * view) — attachment chips render without download links.
   */
  downloadable?: boolean
}

export function RunRequest({ input, questions = [], defaultOpen = false, downloadable = true }: RunRequestProps) {
  const { t, formatAgo } = useI18n()
  const [open, setOpen] = useState(defaultOpen)
  const bodyId = useId()

  const parts: string[] = []
  if (input.attachments.length > 0) parts.push(t.run.attachmentsCount(input.attachments.length))
  const answered = input.replies.filter((r) => r.trigger === 'reply').length
  if (answered > 0) parts.push(t.run.repliesCount(answered))

  return (
    <section className={`run-request card${open ? ' is-open' : ''}`} aria-label={t.run.yourRequest}>
      <button
        type="button"
        className="run-request-summary"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="chevron" size={13} className="run-request-chevron" />
        <span className="field-label">{t.run.yourRequest}</span>
        <span className="run-request-preview">{oneLine(input.brief) || t.run.noBriefText}</span>
        {parts.length > 0 && <span className="run-request-count muted">{parts.join(' · ')}</span>}
        <span className="muted run-request-meta">
          {input.startedBy && <>{input.startedBy.displayName || input.startedBy.email} · </>}
          {formatAgo(input.createdAt)}
        </span>
      </button>

      {open && (
        <div className="run-request-body" id={bodyId}>
          {input.brief ? (
            <p className="run-request-brief" style={{ whiteSpace: 'pre-wrap' }}>
              {input.brief}
            </p>
          ) : (
            <p className="muted">{t.run.noBriefText}</p>
          )}
          {input.briefTruncated && <p className="muted">{t.run.briefTruncated}</p>}

          <FileChips
            files={input.attachments}
            urlFor={downloadable ? (f) => runInputFileUrl(input.executionId, f.index) : undefined}
          />

          {input.replies.length > 0 && (
            <div className="run-request-replies">
              {input.replies.map((r) => (
                <QuestionAndReply
                  key={r.index}
                  executionId={input.executionId}
                  reply={r}
                  question={questions.find((q) => q.round === r.index + 1)}
                  downloadable={downloadable}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  )
}

/** One conversation round: the agent's question, then how it was answered. */
function QuestionAndReply({
  executionId,
  reply,
  question,
  downloadable
}: {
  executionId: string
  reply: RunInputReplyView
  question?: AskedQuestion
  downloadable: boolean
}) {
  const { t, formatAgo } = useI18n()
  return (
    <div className="run-request-round">
      <div className="qa-q">
        <span className="qa-glyph" aria-hidden="true">
          <Icon name="prompt" size={13} />
        </span>
        <div className="qa-q-body">
          <div className="qa-label">{t.run.questionN(reply.index + 1)}</div>
          {question?.prompt ? (
            <p className="qa-text">{question.prompt}</p>
          ) : (
            <p className="qa-text muted">{t.run.agentAskedForInput}</p>
          )}
        </div>
      </div>
      <div className="qa-a">
        <div className="run-request-reply-head">
          {reply.trigger === 'reply' ? (
            <span className="chip">{t.run.yourReplyChip}</span>
          ) : (
            <span className="chip">{reply.trigger === 'timeout' ? t.run.noReplyTimedOut : t.run.cancelled}</span>
          )}
          <span className="muted">{formatAgo(reply.at)}</span>
        </div>
        {reply.rawBody && (
          <p className="run-request-brief" style={{ whiteSpace: 'pre-wrap' }}>
            {reply.rawBody}
          </p>
        )}
        <FileChips
          files={reply.attachments}
          urlFor={downloadable ? (f) => runReplyFileUrl(executionId, reply.index, f.index) : undefined}
        />
      </div>
    </div>
  )
}

/** Attachment chips; without `urlFor` they render inert (pre-persistence). */
function FileChips({ files, urlFor }: { files: RunFileMeta[]; urlFor?: (f: RunFileMeta) => string }) {
  const { formatBytes } = useI18n()
  if (files.length === 0) return null
  return (
    <div className="chips" style={{ marginTop: 8 }}>
      {files.map((f) => {
        const inner = (
          <>
            <Icon name={iconForMime(f.mimetype)} size={12} />
            <span className="attach-name">{f.name}</span>
            <span className="muted">{formatBytes(f.size)}</span>
          </>
        )
        return urlFor ? (
          <a
            key={f.index}
            href={urlFor(f)}
            download={f.name}
            className="chip"
            title={`${f.mimetype} · ${formatBytes(f.size)}`}
          >
            {inner}
            <Icon name="download" size={11} />
          </a>
        ) : (
          <span key={f.index} className="chip" title={`${f.mimetype} · ${formatBytes(f.size)}`}>
            {inner}
          </span>
        )
      })}
    </div>
  )
}

function oneLine(s: string): string {
  const flat = s.replace(/\s+/g, ' ').trim()
  return flat.length <= 140 ? flat : `${flat.slice(0, 139).trimEnd()}…`
}
